import queryString from "qs";
import {
  CreateSnapshotEventMailPayload,
  GetSnapshotEventGroupedFiltersResponse,
  GetSnapshotEventGroupedResponseItem,
  GetSnapshotEventResponseItem,
  SnapshotEventMail,
  UpdateSnapshotEventMailPayload,
} from "@/modules/snapshots/businessReports.types";
import { CreateSnapshotEventRequest, UpdateSnapshotEventRequest } from "@/modules/snapshots/snapshotCollection.types";
import { BUSINESS_REPORTS_TAGS } from "@/modules/snapshots/businessReports.constraints";
import { SnapshotEventsQueryArgs } from "./types";
import { exportsApi } from "./exports";

interface SnapshotEventsQueryPayload {
  business_report_outcome_id?: string[];
  document_name?: string;
  business_report_outcome_result_id?: string;
}

export const buildSnapshotEventsQuery = ({
  businessReportOutcomeId,
  documentName,
  businessReportOutcomeResultId,
}: SnapshotEventsQueryArgs) => {
  const data: SnapshotEventsQueryPayload = {};
  const businessReportOutcomeIds = Array.isArray(businessReportOutcomeId)
    ? businessReportOutcomeId
    : businessReportOutcomeId
      ? [businessReportOutcomeId]
      : [];

  if (businessReportOutcomeIds.length > 0) {
    data.business_report_outcome_id = businessReportOutcomeIds;
  }
  if (documentName) {
    data.document_name = documentName;
  }
  if (businessReportOutcomeResultId) {
    data.business_report_outcome_result_id = businessReportOutcomeResultId;
  }

  return {
    url: "/pf-mainweb/business-reports/snapshot-events/query",
    method: "POST",
    data,
  };
};

/** Snapshot events, their reminders and scheduled event mails. */
export const snapshotEventsApi = exportsApi.injectEndpoints({
  overrideExisting: false,
  endpoints: builder => ({
    getSnapshotEvents: builder.query<GetSnapshotEventResponseItem[], SnapshotEventsQueryArgs>({
      providesTags: [BUSINESS_REPORTS_TAGS.SNAPSHOT_EVENTS_TAG],
      query: buildSnapshotEventsQuery,
    }),
    createSnapshotEvent: builder.mutation<void, CreateSnapshotEventRequest>({
      invalidatesTags: [BUSINESS_REPORTS_TAGS.SNAPSHOT_EVENTS_TAG],
      query: data => ({
        url: "/pf-mainweb/business-reports/snapshot-events",
        method: "POST",
        data,
      }),
    }),
    updateSnapshotEvent: builder.mutation<void, UpdateSnapshotEventRequest>({
      invalidatesTags: [BUSINESS_REPORTS_TAGS.SNAPSHOT_EVENTS_TAG],
      query: data => ({
        url: "/pf-mainweb/business-reports/snapshot-events",
        method: "PUT",
        data,
      }),
    }),
    sendSnapshotEventRemind: builder.mutation<void, string>({
      invalidatesTags: [BUSINESS_REPORTS_TAGS.SNAPSHOT_EVENTS_TAG],
      query: id => ({
        url: `/pf-mainweb/business-reports/snapshot-events/remind/${id}`,
        method: "POST",
      }),
    }),
    sendSnapshotEventRecipientRemind: builder.mutation<void, string>({
      invalidatesTags: [BUSINESS_REPORTS_TAGS.SNAPSHOT_EVENTS_TAG],
      query: id => ({
        url: `/pf-mainweb/business-reports/snapshot-events/recipients/remind/${id}`,
        method: "POST",
      }),
    }),
    makeSnapshotEventCompleted: builder.mutation<void, string>({
      invalidatesTags: [BUSINESS_REPORTS_TAGS.SNAPSHOT_EVENTS_TAG],
      query: id => ({
        url: `/pf-mainweb/business-reports/snapshot-events/completed/${id}`,
        method: "PUT",
      }),
    }),
    makeSnapshotEventRecipientCompleted: builder.mutation<void, string>({
      invalidatesTags: [BUSINESS_REPORTS_TAGS.SNAPSHOT_EVENTS_TAG],
      query: id => ({
        url: `/pf-mainweb/business-reports/snapshot-events/recipients/completed/${id}`,
        method: "PUT",
      }),
    }),
    deleteSnapshotEvent: builder.mutation<unknown, string>({
      invalidatesTags: [BUSINESS_REPORTS_TAGS.SNAPSHOT_EVENTS_TAG],
      query: id => ({
        url: `/pf-mainweb/business-reports/snapshot-events/${id}`,
        method: "DELETE",
      }),
    }),
    getSnapshotEventGrouped: builder.query<
      {
        data: Record<string, Array<GetSnapshotEventGroupedResponseItem>>;
        metadata: {
          startDate: number;
          endDate: number;
          monthMarkers: Array<number>;
        };
      },
      {
        document_name?: string;
        status?: string;
        snapshot_collection_id?: string;
        prompt_id?: string;
        event_date_gte?: string;
        event_date_lte?: string;
        action_taker?: string;
        creator?: string;
        without_event?: boolean;
        sort_field: string;
        sort_order: string;
      }
    >({
      providesTags: [BUSINESS_REPORTS_TAGS.SNAPSHOT_EVENTS_TAG],
      query: queryParams => {
        const query = queryString.stringify(queryParams);
        return {
          url: `/pf-mainweb/business-reports/snapshot-events-grouped?${query}`,
        };
      },
      transformResponse: (response: Record<string, Array<GetSnapshotEventGroupedResponseItem>>) => {
        const allItems = Object.values(response).flat();

        const startTimestamp = Math.min(...allItems.map(({ event_date }) => +new Date(event_date)));
        const endTimestamp = Math.max(...allItems.map(({ event_date }) => +new Date(event_date)));

        return {
          data: response,
          metadata: {
            startDate: startTimestamp,
            endDate: endTimestamp,
            monthMarkers: (() => {
              const x = Math.round((endTimestamp - startTimestamp) / (1000 * 60 * 60 * 24 * 30));

              const result = [];
              for (let i = 0; i < x; i++) {
                result.push(startTimestamp + i * 1000 * 60 * 60 * 24 * 30);
              }
              result.push(startTimestamp + x * 1000 * 60 * 60 * 24 * 30);

              return result;
            })(),
          },
        };
      },
    }),
    getSnapshotEventGroupedFilters: builder.query<GetSnapshotEventGroupedFiltersResponse, void>({
      providesTags: [BUSINESS_REPORTS_TAGS.SNAPSHOT_EVENTS_TAG],
      query: () => ({
        url: `/pf-mainweb/business-reports/snapshot-events-grouped-filters`,
      }),
    }),
    getSnapshotEventMails: builder.query<SnapshotEventMail[], void>({
      providesTags: [BUSINESS_REPORTS_TAGS.SNAPSHOT_EVENT_MAILS_TAG],
      query: () => ({
        url: "/pf-mainweb/business-reports/snapshot-event-mails",
      }),
    }),
    createSnapshotEventMail: builder.mutation<SnapshotEventMail, CreateSnapshotEventMailPayload>({
      invalidatesTags: [BUSINESS_REPORTS_TAGS.SNAPSHOT_EVENT_MAILS_TAG],
      query: data => ({
        url: "/pf-mainweb/business-reports/snapshot-event-mails",
        method: "POST",
        data,
      }),
    }),
    updateSnapshotEventMail: builder.mutation<SnapshotEventMail, { id: string; data: UpdateSnapshotEventMailPayload }>({
      invalidatesTags: [BUSINESS_REPORTS_TAGS.SNAPSHOT_EVENT_MAILS_TAG],
      query: ({ id, data }) => ({
        url: `/pf-mainweb/business-reports/snapshot-event-mails/${id}`,
        method: "PUT",
        data,
      }),
    }),
    deleteSnapshotEventMail: builder.mutation<void, string>({
      invalidatesTags: [BUSINESS_REPORTS_TAGS.SNAPSHOT_EVENT_MAILS_TAG],
      query: id => ({
        url: `/pf-mainweb/business-reports/snapshot-event-mails/${id}`,
        method: "DELETE",
      }),
    }),
  }),
});

export const {
  useGetSnapshotEventsQuery,
  useCreateSnapshotEventMutation,
  useUpdateSnapshotEventMutation,
  useSendSnapshotEventRemindMutation,
  useSendSnapshotEventRecipientRemindMutation,
  useMakeSnapshotEventCompletedMutation,
  useMakeSnapshotEventRecipientCompletedMutation,
  useDeleteSnapshotEventMutation,
  useGetSnapshotEventGroupedQuery,
  useGetSnapshotEventGroupedFiltersQuery,
  useGetSnapshotEventMailsQuery,
  useCreateSnapshotEventMailMutation,
  useUpdateSnapshotEventMailMutation,
  useDeleteSnapshotEventMailMutation,
} = snapshotEventsApi;
